const MAX_PHONE_LENGTH = 15;
const PHONE_NUMBER_SECTIONS = [3, 7];
const DASHED_CURSOR_FORWARD_MAP: Record<number, number> = { 3: 5, 8: 10 };
const DASHED_CURSOR_BACKWARD_MAP: Record<number, number> = { 4: 2, 9: 7 };

/**
 * Helper functions for phone number formatting and field management.
 * @param value raw phone number value
 * @param variant "DEFAULT" | "WITH_DASHED" - whether to format the phone number with dashes
 * @returns formatted phone number value
 */
export function getPhoneValue(
  value: string | undefined,
  variant: "DEFAULT" | "WITH_DASHED"
): string {
  if (!value) return "";
  const cleaned = value.slice(0, MAX_PHONE_LENGTH).replace(/[^\d]/g, "");
  if (variant === "WITH_DASHED") {
    if (cleaned.length > PHONE_NUMBER_SECTIONS[1]) {
      return `${cleaned.slice(0, PHONE_NUMBER_SECTIONS[0])}-${cleaned.slice(
        PHONE_NUMBER_SECTIONS[0],
        PHONE_NUMBER_SECTIONS[1]
      )}-${cleaned.slice(PHONE_NUMBER_SECTIONS[1])}`;
    }
    if (cleaned.length > PHONE_NUMBER_SECTIONS[0]) {
      return `${cleaned.slice(0, PHONE_NUMBER_SECTIONS[0])}-${cleaned.slice(
        PHONE_NUMBER_SECTIONS[0]
      )}`;
    }
  }
  return cleaned;
}

/**
 * Formats the label for a field, appending an asterisk if the field is required.
 * @param label label for the field
 * @param isRequired whether the field is required
 * @returns formatted label with an asterisk if required
 */
export function getLabel(label: string | undefined, isRequired: boolean) {
  return label ? `${label}${isRequired ? " *" : ""}` : "";
}

/**
 * Transforms the phone number input value based on the raw input,
 * @param raw raw input value
 * @param value current phone number value
 * @param selection current selection in the input (cursor location)
 * @param variant "DEFAULT" | "WITH_DASHED" - whether to format the phone number with dashes
 * @returns { cleaned: string, cursor: { start: number, end: number } }
 */
export function transformPhoneValue(
  raw: string,
  value: string | undefined = "",
  selection: { start: number; end: number },
  variant: "DEFAULT" | "WITH_DASHED"
) {
  const isWithDashed = variant === "WITH_DASHED";
  const isDeleted = raw.length < getPhoneValue(value, variant).length;

  let cleaned = raw.replace(/[^\d]/g, "").replace(/^0/, "");
  if (cleaned.length > MAX_PHONE_LENGTH)
    return { cleaned: value, cursor: selection };

  const diff = cleaned.length - value.length;
  let cursor = selection.start;

  // Without dashed, we can just return the cleaned value and adjust the cursor
  if (!isWithDashed) {
    const jump = diff ? (isDeleted ? -1 : 1) : 0;
    return { cleaned, cursor: { start: cursor + jump, end: cursor + jump } };
  }

  // With dashed, we need to handle the dashes and adjust the cursor accordingly
  // If detected as deleted, we need to jump the dash and cleaned the correct value
  if (isDeleted) {
    if (cursor === PHONE_NUMBER_SECTIONS[0] + 1)
      // Jump over the first dash
      cleaned =
        cleaned.slice(0, PHONE_NUMBER_SECTIONS[0] - 1) +
        cleaned.slice(PHONE_NUMBER_SECTIONS[0] + 1);
    else if (cursor === PHONE_NUMBER_SECTIONS[1] + 2)
      // Jump over the second dash
      cleaned =
        cleaned.slice(0, PHONE_NUMBER_SECTIONS[1] - 1) +
        cleaned.slice(PHONE_NUMBER_SECTIONS[1] + 1);
  }

  cursor = isDeleted
    ? DASHED_CURSOR_BACKWARD_MAP[cursor] ?? cursor - 1
    : DASHED_CURSOR_FORWARD_MAP[cursor] ?? cursor + 1;
  return { cleaned, cursor: { start: cursor, end: cursor } };
}
